import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "../../App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ArrowLeft, Mail, Shield, Lock, Eye, EyeOff } from "lucide-react";

// Password input with auto-hide visibility toggle
function PasswordInput({ value, onChange, placeholder, ...props }) {
  const [showPassword, setShowPassword] = useState(false);

  return (
    <div className="relative">
      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
      <Input
        type={showPassword ? "text" : "password"}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="pl-10 pr-10"
        {...props}
      />
      <button
        type="button"
        onClick={() => setShowPassword(!showPassword)}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
      >
        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
  );
}

export function AdminLoginForm({ onBack, portal = "admin", redirectTo = "/admin/dashboard", title = "Admin Login", subtitle = "Access the admin dashboard" }) {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 1: login, 2: otp
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetCode, setResetCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [resetStep, setResetStep] = useState(1);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) {
      toast.error("Please enter both email and password");
      return;
    }
    setLoading(true);
    try {
      // Use AbortController for timeout and a fresh fetch context
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000);
      
      let res, data = {};
      try {
        res = await fetch(`${API}/admin/auth/login`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ email: email.trim().toLowerCase(), password, portal }),
          signal: controller.signal
        });
        clearTimeout(timeoutId);
        
        // Parse JSON response
        data = await res.json();
      } catch (fetchError) {
        clearTimeout(timeoutId);
        // Handle fetch/parse errors - determine cause from status if available
        if (res) {
          // Response exists but JSON parsing failed
          if (res.status === 401) {
            toast.error("Incorrect password. Please try again.");
            return;
          } else if (res.status === 404) {
            toast.error("No admin account found with this email.");
            return;
          } else if (res.status === 423) {
            toast.error("Account locked due to too many failed attempts. Please reset your password.");
            setShowForgotPassword(true);
            return;
          } else if (!res.ok) {
            toast.error("Login failed. Please try again.");
            return;
          }
        }
        // Network error or abort
        console.error("Login fetch error:", fetchError);
        toast.error("Network error. Please check your connection.");
        return;
      }
      
      if (res.ok && data.success && data.requires_otp) {
        toast.success("Verification code sent to admin email");
        setStep(2);
      } else if (res.ok && data.success && !data.requires_otp) {
        // 2FA is disabled - direct login successful
        toast.success("Login successful!");
        setTimeout(() => {
          navigate(redirectTo);
        }, 100);
      } else if (res.status === 401) {
        toast.error(data.detail || "Incorrect password. Please try again.");
      } else if (res.status === 404) {
        toast.error(data.detail || "No admin account found with this email.");
      } else if (res.status === 423) {
        // Account locked due to too many failed attempts
        toast.error(data.detail || "Account locked. Please reset your password.");
        setShowForgotPassword(true);
      } else if (!res.ok) {
        toast.error(data.detail || "Login failed. Please try again.");
      }
    } catch (error) {
      console.error("Login error:", error);
      toast.error("Network error. Please check your connection.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (!otp.trim() || otp.length !== 6) {
      toast.error("Please enter the 6-digit code");
      return;
    }
    setLoading(true);
    
    // Use AbortController for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 15000);
    
    let res, data = {};
    try {
      res = await fetch(`${API}/admin/auth/verify-login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim().toLowerCase(), otp_code: otp.trim(), portal }),
        credentials: "include",
        signal: controller.signal
      });
      clearTimeout(timeoutId);
      
      // Parse JSON response
      data = await res.json();
    } catch (fetchError) {
      clearTimeout(timeoutId);
      // Handle fetch/parse errors - determine cause from status if available
      if (res) {
        if (res.status === 400) {
          toast.error("Invalid or expired code. Please login again.");
          setStep(1);
          setOtp("");
          return;
        } else if (res.status === 404) {
          toast.error("Admin account not found.");
          setStep(1);
          return;
        } else if (!res.ok) {
          toast.error("Verification failed. Please try again.");
          return;
        }
      }
      console.error("Admin verify OTP error:", fetchError);
      toast.error("Connection error. Please check your internet and try again.");
      return;
    } finally {
      setLoading(false);
    }
    
    if (res.ok && data.success) {
      // Tokens are now stored securely in HttpOnly cookies by the server
      // No localStorage storage needed - more secure against XSS attacks
      toast.success("Login successful!");
      // Small delay to ensure cookies are set before navigation
      setTimeout(() => {
        navigate(redirectTo);
      }, 100);
    } else if (res.status === 400) {
      toast.error(data.detail || "Invalid or expired code. Please login again.");
      setStep(1);
      setOtp("");
    } else if (res.status === 404) {
      toast.error(data.detail || "Admin account not found.");
      setStep(1);
    } else {
      toast.error(data.detail || data.message || "Verification failed. Please try again.");
    }
  };

  const handleForgotPassword = async () => {
    if (!email.trim()) {
      toast.error("Please enter your email first");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/auth/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim().toLowerCase() })
      });
      const data = await res.json();
      if (data.success) {
        toast.success("Reset code sent to admin verification email");
        setResetStep(2);
      } else {
        toast.error(data.detail || "Failed to send reset code");
      }
    } catch {
      toast.error("Network error");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!resetCode.trim() || !newPassword.trim()) {
      toast.error("Please fill all fields");
      return;
    }
    if (newPassword.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          email: email.trim().toLowerCase(), 
          otp_code: resetCode.trim(),
          new_password: newPassword 
        })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success("Password reset successfully! You can now login.");
        setShowForgotPassword(false);
        setResetStep(1);
        setResetCode("");
        setNewPassword("");
      } else {
        toast.error(data.detail || data.message || "Reset failed. Please try again.");
      }
    } catch (error) {
      console.error("Reset password error:", error);
      toast.error("Network error. Please check your connection and try again.");
    } finally {
      setLoading(false);
    }
  };

  if (showForgotPassword) {
    return (
      <div className="space-y-6">
        <button 
          onClick={() => { setShowForgotPassword(false); setResetStep(1); }}
          className="flex items-center text-gray-600 hover:text-gray-800 transition-colors"
        >
          <ArrowLeft className="h-4 w-4 mr-2" /> Back to login
        </button>
        
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900">Reset Admin Password</h2>
          <p className="text-gray-600 mt-2">Reset code will be sent to admin verification email</p>
        </div>

        {resetStep === 1 && (
          <div className="space-y-4">
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Admin email address"
                className="pl-10"
              />
            </div>
            
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Mail className="h-5 w-5 text-amber-600 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-800">Email Only</p>
                  <p className="text-xs text-amber-600">Reset code will be sent to admin@contentiumtech.com</p>
                </div>
              </div>
            </div>
            
            <Button 
              onClick={handleForgotPassword} 
              disabled={loading}
              className="w-full bg-indigo-600 hover:bg-indigo-700"
            >
              {loading ? "Sending..." : "Send Reset Code"}
            </Button>
          </div>
        )}

        {resetStep === 2 && (
          <div className="space-y-4">
            <Input
              value={resetCode}
              onChange={(e) => setResetCode(e.target.value)}
              placeholder="Enter 6-digit code"
              className="text-center text-2xl tracking-widest"
              maxLength={6}
            />
            <PasswordInput
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="New password (min 8 characters)"
            />
            <Button 
              onClick={handleResetPassword}
              disabled={loading}
              className="w-full bg-indigo-600 hover:bg-indigo-700"
            >
              {loading ? "Resetting..." : "Reset Password"}
            </Button>
          </div>
        )}
      </div>
    );
  }

  // OTP Verification Step
  if (step === 2) {
    return (
      <div className="space-y-6">
        <button 
          onClick={() => setStep(1)}
          className="flex items-center text-gray-600 hover:text-gray-800 transition-colors"
        >
          <ArrowLeft className="h-4 w-4 mr-2" /> Back
        </button>

        <div className="text-center">
          <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Shield className="h-8 w-8 text-indigo-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900">Admin Verification</h2>
          <p className="text-gray-600 mt-2">Enter the code sent to admin verification email</p>
        </div>

        <Input
          value={otp}
          onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
          placeholder="000000"
          className="text-center text-3xl tracking-[0.5em] font-mono"
          maxLength={6}
          data-testid="admin-otp-input"
        />

        <Button 
          onClick={handleVerifyOTP}
          disabled={loading || otp.length !== 6}
          className="w-full bg-indigo-600 hover:bg-indigo-700"
          data-testid="admin-verify-otp-btn"
        >
          {loading ? "Verifying..." : "Verify & Login"}
        </Button>

        <p className="text-center text-sm text-gray-500">
          Didn't receive code? <button onClick={() => handleLogin({ preventDefault: () => {} })} className="text-indigo-600 hover:underline">Resend</button>
        </p>
      </div>
    );
  }

  // Login Form
  return (
    <form onSubmit={handleLogin} className="space-y-6">
      <button 
        type="button"
        onClick={onBack}
        className="flex items-center text-gray-600 hover:text-gray-800 transition-colors"
      >
        <ArrowLeft className="h-4 w-4 mr-2" /> Back
      </button>

      <div className="text-center">
        <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Shield className="h-8 w-8 text-indigo-600" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
        <p className="text-gray-600 mt-2">{subtitle}</p>
      </div>

      <div className="space-y-4">
        <div className="relative">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Admin email"
            className="pl-10"
            data-testid="admin-email-input"
          />
        </div>
        
        <PasswordInput
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Password"
          data-testid="admin-password-input"
        />
      </div>

      <Button 
        type="submit" 
        disabled={loading}
        className="w-full bg-indigo-600 hover:bg-indigo-700"
        data-testid="admin-login-btn"
      >
        {loading ? "Verifying..." : "Continue"}
      </Button>

      <button
        type="button"
        onClick={() => setShowForgotPassword(true)}
        className="w-full text-sm text-indigo-600 hover:text-indigo-700"
      >
        Forgot Password?
      </button>
    </form>
  );
}
